import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// =============================================================================
// Events store — buffer alarm events (in-memory, max 500 entry, ring buffer).
// Events masuk dari WS handler (lihat components/notifications/event-notifier).
//
// Filter: search (cocokkan ke vehicle name/alarm name) + alarmIds (white-list).
// matchesFilter() dipakai TIGA tempat:
//   - daftar di EventsPanel (visible row)
//   - toast popup notifikasi (cuma yang lolos filter yang pop)
//   - alarm sound (cuma yang lolos filter yang bunyi)
// =============================================================================

export interface AlarmEvent {
  id: string;              // unique per event (vehicleId+ts atau "db-<rowId>")
  /** DB row id kalau berasal dari tracks_alarm — diperlukan untuk DELETE call. */
  dbId?: number;
  vehicleId: number;
  vehicleName: string;
  imei?: string;
  alarmId: number;
  alarmName: string;
  lat: number;
  lng: number;
  speed?: number;
  address?: string;
  /** Nama geofence saat event (Masuk/Keluar Geofence). */
  geofence?: string;
  timestamp: string;       // ISO
}

export interface EventFilter {
  /** Free-text search — cocokkan ke vehicleName + alarmName (case-insensitive). */
  search: string;
  /** White-list alarm code; empty = semua di-allow. */
  alarmIds: number[];
}

const MAX_EVENTS = 500;

interface EventsState {
  events: AlarmEvent[];     // newest first
  filter: EventFilter;
  /** Whitelist alarmId yang BOLEH trigger toast popup + sound. Empty = semua
   *  di-allow (default). Beda dengan filter.alarmIds yg control list display. */
  notifyAlarmIds: number[];
  /** Pending event id untuk di-pick saat user buka monitoring (via "Show event"
   *  link di notification toast). Monitoring page baca + clear setelah dipakai. */
  pendingPickId: string | null;

  add: (ev: AlarmEvent) => void;
  /** Bulk replace — dipakai initial load history dari /api/v1/alarm/recent. */
  setAll: (events: AlarmEvent[]) => void;
  /** Hapus 1 event berdasar id store. */
  remove: (id: string) => void;
  /** Hapus banyak event sekaligus. */
  removeMany: (ids: string[]) => void;
  /** Isi alamat hasil reverse-geocode on-demand (kolom Address events panel). */
  setAddress: (id: string, address: string) => void;
  clear: () => void;
  setFilter: (patch: Partial<EventFilter>) => void;
  matchesFilter: (ev: AlarmEvent) => boolean;
  /** Set whitelist alarmIds yang trigger notifikasi+sound. */
  setNotifyAlarmIds: (ids: number[]) => void;
  /** Toggle 1 alarmId di whitelist notifikasi. */
  toggleNotifyAlarm: (id: number) => void;
  /** True kalau alarmId di-allow utk notifikasi. Empty whitelist = allow all. */
  isNotifyAllowed: (alarmId: number) => boolean;
  setPendingPickId: (id: string | null) => void;
}

// Persist hanya notifyAlarmIds (preferensi user) — events ring buffer tetap
// in-memory supaya tidak meledak localStorage.
export const useEventsStore = create<EventsState>()(persist(
  (set, get) => ({
  events: [],
  filter: { search: '', alarmIds: [] },
  notifyAlarmIds: [],
  pendingPickId: null,

  add: (ev) => set((s) => {
    // Ring buffer: tambah di depan, potong ekor kalau sudah max.
    // Dedupe by id supaya WS event yang ke-arrived setelah initial load tidak
    // muncul dobel.
    if (s.events.some((e) => e.id === ev.id)) return s;
    const next = [ev, ...s.events];
    if (next.length > MAX_EVENTS) next.length = MAX_EVENTS;
    return { events: next };
  }),

  setAll: (events) => set(() => {
    const next = [...events];
    if (next.length > MAX_EVENTS) next.length = MAX_EVENTS;
    return { events: next };
  }),

  remove: (id) => set((s) => ({ events: s.events.filter((e) => e.id !== id) })),

  removeMany: (ids) => set((s) => {
    const set = new Set(ids);
    return { events: s.events.filter((e) => !set.has(e.id)) };
  }),

  setAddress: (id, address) => set((s) => ({
    events: s.events.map((e) => (e.id === id ? { ...e, address } : e)),
  })),

  clear: () => set({ events: [] }),

  setFilter: (patch) => set((s) => ({ filter: { ...s.filter, ...patch } })),

  matchesFilter: (ev) => {
    const { search, alarmIds } = get().filter;
    if (alarmIds.length > 0 && !alarmIds.includes(ev.alarmId)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!ev.vehicleName.toLowerCase().includes(q) &&
          !ev.alarmName.toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  },

  setNotifyAlarmIds: (ids) => set({ notifyAlarmIds: ids }),
  toggleNotifyAlarm: (id) => set((s) => {
    const has = s.notifyAlarmIds.includes(id);
    return { notifyAlarmIds: has ? s.notifyAlarmIds.filter((x) => x !== id) : [...s.notifyAlarmIds, id] };
  }),
  isNotifyAllowed: (alarmId) => {
    const ids = get().notifyAlarmIds;
    if (ids.length === 0) return true; // empty whitelist = allow all
    return ids.includes(alarmId);
  },
  setPendingPickId: (id) => set({ pendingPickId: id }),
  }),
  {
    name: 'webgps-events-prefs',
    // Persist HANYA notifyAlarmIds — bukan events (ring buffer in-memory).
    partialize: (s) => ({ notifyAlarmIds: s.notifyAlarmIds }),
  },
));
